// The one typed HTTP client for this app: no openapi-fetch, no orval — a
// single `http` object built over fetch. Every request is prefixed with
// env.apiBaseUrl and carries `credentials: "include"` so the SSO cookie
// rides along; every non-OK response is normalized via parseErrorResponse.

import { env } from "@/lib/env";
import { parseErrorResponse } from "./transport";

export interface HttpOpts {
    /** JSON-encodable payload. Omitted means no body and no Content-Type header. */
    json?: unknown;
}

async function request<T>(method: string, path: string, opts: HttpOpts = {}): Promise<T> {
    const { json } = opts;
    const headers: Record<string, string> = json !== undefined ? { "Content-Type": "application/json" } : {};

    const res = await fetch(`${env.apiBaseUrl}${path}`, {
        method,
        credentials: "include",
        headers,
        body: json !== undefined ? JSON.stringify(json) : undefined,
    });

    if (!res.ok) {
        throw await parseErrorResponse(res);
    }

    // 204 No Content (DELETE, unlink-template): response.json() would reject.
    if (res.status === 204) {
        return undefined as T;
    }

    // Some 2xx responses carry an empty body despite not being 204 (e.g. a
    // proxy stripping it) — treat an empty body the same way rather than
    // letting JSON.parse throw on "".
    const text = await res.text();
    if (text.length === 0) {
        return undefined as T;
    }
    return JSON.parse(text) as T;
}

export const http = {
    get: <T>(path: string, opts?: HttpOpts): Promise<T> => request<T>("GET", path, opts),
    post: <T>(path: string, opts?: HttpOpts): Promise<T> => request<T>("POST", path, opts),
    patch: <T>(path: string, opts?: HttpOpts): Promise<T> => request<T>("PATCH", path, opts),
    delete: <T>(path: string, opts?: HttpOpts): Promise<T> => request<T>("DELETE", path, opts),
};
